import {
  CreditScoreSegmentSource,
  CreditScoreSpreadPointDto,
  CreditScoreSpreadsDto,
} from '@interfaces/credit-score/responses/credit-score.response';

/** Formatação pt-BR e utilidades de exibição do score de crédito (card 3766). Sem conta financeira. */

/** Prazo da coluna "Spread 5 anos" da carteira. */
export const PORTFOLIO_SPREAD_MONTHS = 60;

/** Prazos da curva de spread CMA do rating: 1 a 11 anos (como na página do Felipe). */
export const SPREAD_CURVE_TENORS: readonly number[] = Array.from(
  { length: 11 },
  (_, index) => (index + 1) * 12
);

/** Nomes das métricas gerais para o cliente (sem sigla técnica em inglês). */
export const CLIENT_METRIC_LABELS: Record<string, string> = {
  netDebtEbitda: 'Dívida líquida / EBITDA',
  interestCoverage: 'Cobertura de juros',
  currentRatio: 'Liquidez corrente',
  operatingCashFlowDebt: 'FCO / dívida bruta',
  ebitdaMargin: 'Margem EBITDA',
  revenueInstability3y: 'Instabilidade da receita (3 anos)',
};

/** Número pt-BR com casas fixas; "—" sem valor. */
export function formatDecimal(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Valor da métrica com a unidade da metodologia ("2,10 x"); "N/D" sem valor. */
export function formatMetricValue(
  value: number | null | undefined,
  unit: string | null | undefined,
  digits = 2
): string {
  if (value == null || !Number.isFinite(Number(value))) return 'N/D';
  return `${formatDecimal(value, digits)}${unit ? ` ${unit}` : ''}`;
}

/**
 * Valor da métrica no formato curto do bloco do cliente: múltiplo "2,1x", percentual "14,2%";
 * outras unidades como em `formatMetricValue`. "—" sem valor.
 */
export function formatClientMetricValue(
  value: number | null | undefined,
  unit: string | null | undefined
): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const normalized = (unit ?? '').trim().toLowerCase();
  if (normalized === 'x') return `${formatDecimal(value, 2)}x`;
  if (normalized === '%') return `${formatDecimal(value, 1)}%`;
  return formatMetricValue(value, unit);
}

/** Nome da métrica para o cliente: o nome em português da casa, ou o rótulo da metodologia. */
export function clientMetricLabel(metric: { key: string; label: string }): string {
  return CLIENT_METRIC_LABELS[metric.key] ?? metric.label;
}

/** "A, B e C". */
export function joinWithAnd(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} e ${items.at(-1)}`;
}

/** "+17 pts", "-8 pts", "0 pts"; "—" sem valor. */
export function formatSignedPoints(value: number | null | undefined, digits = 0): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const text = formatDecimal(value, digits);
  return `${Number(value) > 0 ? '+' : ''}${text} pts`;
}

/**
 * Data de referência da curva de spread para um exercício: 31/12 do ano quando é passado; null
 * (último fechamento) no ano corrente.
 */
export function spreadDateForYear(
  year: number | null | undefined,
  currentYear: number
): string | null {
  const value = Number(year);
  return value && value < currentYear ? `${value}-12-31` : null;
}

/** Chave do cache de spreads por prazo e data. */
export function spreadCacheKey(months: number, date: string | null | undefined): string {
  return `${date || 'previous'}|${Number(months)}`;
}

/** Ponto do rating S&P numa resposta de spreads. */
export function creditSpreadPoint(
  payload: CreditScoreSpreadsDto | null | undefined,
  rating: string
): CreditScoreSpreadPointDto | null {
  return payload?.points?.find(point => point.rating === rating) ?? null;
}

/** Origem do spread, como na página do Felipe. */
export function creditSpreadSourceLabel(
  point: CreditScoreSpreadPointDto | null | undefined
): string {
  if (point?.source === 'observed') return 'CMA observado';
  if (point?.source === 'interpolated') return 'Interpolado da CMA';
  return 'Estimado da CMA';
}

/** De onde veio o segmento da empresa. */
export function segmentSourceLabel(source: CreditScoreSegmentSource, persisted: boolean): string {
  if (persisted) return source === 'sector' ? 'Setor do cadastro' : 'Salvo no Cognito';
  if (source === 'pending') return 'Sem segmento: classifique';
  return 'Sugestão, ainda não gravada';
}

/** "1 exercício" / "3 exercícios". */
export function exercisesLabel(count: number): string {
  return `${count} ${count === 1 ? 'exercício' : 'exercícios'}`;
}

/** Escapa texto que vem de dado antes de entrar num balão montado em HTML. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Conteúdo do balão escuro dos Detalhes do Empréstimo (`.maturity-cost-tooltip-content`): título em
 * negrito e uma linha por item. O título é escapado aqui; as linhas já chegam com o dado escapado e
 * podem trazer `<b>` para destacar o número.
 */
export function tooltipHtml(title: string, lines: string[]): string {
  const body = lines.map(line => `<span>${line}</span>`).join('');
  return `<div class="maturity-cost-tooltip-content"><strong>${escapeHtml(title)}</strong>${body}</div>`;
}

/** "1 empresa" / "51 empresas". */
export function companiesLabel(count: number): string {
  return `${count.toLocaleString('pt-BR')} ${count === 1 ? 'empresa' : 'empresas'}`;
}
